import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConnection } from 'wagmi';

import AirplaneIcon from '@/app/assets/svg/airplane.svg?react';
import ErrorIcon from '@/app/assets/svg/error.svg?react';
import InfoIcon from '@/app/assets/svg/info.svg?react';
import { useReferralActions } from '@/app/components/ReferralPageComponents/hooks/useReferralActions';
import { useReferralSummary } from '@/app/components/ReferralPageComponents/hooks/useReferralSummary';
import ReferralActionRow from '@/app/components/ReferralPageComponents/ReferralActionRow';
import { REFERRAL_ACTION_BUTTON_CLASS } from '@/app/components/ReferralPageComponents/referralConstants';
import {
  isOwnReferralCode,
  isValidReferralCodeFormat,
  normalizeReferralCode,
  REFERRAL_ERRORS,
} from '@/app/components/ReferralPageComponents/referralValidation';
import { cn } from '@/app/shared/lib/cn';
import { toReferralApiCode } from '@/app/shared/lib/referralSigning';
import { getReferralCodeCheck } from '@/app/shared/services/api';
import { Button } from '@/app/shared/ui/Button';
import { Input } from '@/app/shared/ui/Input';
import { Skeleton } from '@/app/shared/ui/Skeleton';

export type InviteeBindingState = 'idle' | 'binding' | 'bound';

const ReferralInviteeStatus = () => {
  const [searchParams] = useSearchParams();
  const { isConnected } = useConnection();
  const { openConnectModal } = useConnectModal();
  const {
    code: ownCode,
    referredByCode,
    inviteeActiveBoost,
    isLoading: isSummaryLoading,
  } = useReferralSummary();
  const { bindCode, mapReferralError } = useReferralActions();

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<InviteeBindingState>('idle');
  const [boundCode, setBoundCode] = useState<string | null>(null);

  const resolvedBoundCode =
    boundCode ?? (referredByCode ? normalizeReferralCode(referredByCode) : null);
  const isBoundView = Boolean(resolvedBoundCode);
  const showSummarySkeleton = isConnected && isSummaryLoading;

  useEffect(() => {
    if (isBoundView) return;

    const codeFromUrl = searchParams.get('code');
    if (!codeFromUrl) return;

    setCode(normalizeReferralCode(codeFromUrl));
    setError(null);
  }, [searchParams, isBoundView]);

  const normalizedCode = normalizeReferralCode(code);
  const hasValue = normalizedCode.length > 0;

  const formatError = useMemo(() => {
    if (!hasValue) return null;
    if (!isValidReferralCodeFormat(normalizedCode)) return REFERRAL_ERRORS.INVALID_CODE;
    if (isOwnReferralCode(normalizedCode, ownCode)) return REFERRAL_ERRORS.SELF_REFERRAL;
    return null;
  }, [hasValue, normalizedCode, ownCode]);

  const displayError = error ?? formatError;
  const isBinding = state === 'binding';
  const isBindDisabled =
    isBinding || (isConnected && isSummaryLoading) || !hasValue || !!formatError || !!error;

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setCode(normalizeReferralCode(event.target.value));
  };

  const handleBind = async () => {
    if (!normalizedCode || isBoundView || state !== 'idle' || !!formatError) return;

    if (!isValidReferralCodeFormat(normalizedCode)) {
      setError(REFERRAL_ERRORS.INVALID_CODE);
      return;
    }

    if (isOwnReferralCode(normalizedCode, ownCode)) {
      setError(REFERRAL_ERRORS.SELF_REFERRAL);
      return;
    }

    if (!isConnected) {
      openConnectModal?.();
      return;
    }

    setError(null);
    setState('binding');

    try {
      const check = await getReferralCodeCheck(toReferralApiCode(normalizedCode));
      if (!check?.valid || !check.exists) {
        setError(REFERRAL_ERRORS.INVALID_CODE);
        setState('idle');
        return;
      }

      const result = await bindCode(normalizedCode);
      setBoundCode(normalizeReferralCode(result.code));
      setState('bound');
    } catch (err) {
      setError(mapReferralError(err));
      setState('idle');
    }
  };

  return (
    <ReferralActionRow
      title='Invitee Status'
      description='Bind your account to a referrer code to trigger boosted points on your holdings.'
    >
      {showSummarySkeleton ? (
        <div className='flex flex-col gap-3.5'>
          <Skeleton.Line loading className='h-12 w-full' />
          <Skeleton.Line loading className='h-5 w-3/4' />
        </div>
      ) : isBoundView && resolvedBoundCode ? (
        <div className='flex flex-col gap-3.5'>
          <div className='flex items-center gap-2 rounded-md bg-feedback-green/20 px-4 py-3 text-feedback-green-100 [:root.light_&]:bg-feedback-success/24 [:root.light_&]:text-feedback-success'>
            <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-feedback-green-100/12 [:root.light_&]:bg-feedback-success [:root.light_&]:text-feedback-green-100'>
              <AirplaneIcon className='h-4 w-4' />
            </span>
            <span className='text-base font-normal'>
              Active Binding: {resolvedBoundCode}
              {inviteeActiveBoost ? ` (+${inviteeActiveBoost} Boost)` : ''}
            </span>
          </div>
          <div className='flex min-h-5 items-center gap-1.5 text-sm leading-5 text-gray-400'>
            <InfoIcon className='h-3.5 w-3.5 shrink-0' />
            <span>Boosts apply only to future daily snapshots taken after binding.</span>
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-5 lg:flex-row lg:items-start'>
          <div className='flex min-w-0 flex-1 flex-col gap-2'>
            <Input
              value={code}
              onChange={handleCodeChange}
              placeholder='Enter referral code'
              disabled={isBinding}
              limitLength={false}
              className={cn(
                'w-full rounded-md border border-gray-700 bg-transparent px-4 py-2.75 text-base leading-6 font-normal text-gray-200 placeholder:text-gray-400 focus:border-modal-result-body [:root.light_&]:border-gray-900 [:root.light_&]:focus:border-gray-700',
                !!normalizedCode && !displayError && 'text-brand-1100',
                displayError &&
                  'border-feedback-error text-brand-1100 focus:border-feedback-error [:root.light_&]:border-feedback-error [:root.light_&]:focus:border-feedback-error',
              )}
            />
            {displayError ? (
              <div className='flex items-center gap-1.5 text-sm text-gray-400'>
                <ErrorIcon className='h-4 w-4 shrink-0' />
                <span>{displayError}</span>
              </div>
            ) : (
              <div className='hidden lg:block lg:min-h-6.5' />
            )}
          </div>

          <Button
            variant='ghost'
            onClick={handleBind}
            disabled={isBindDisabled}
            className={cn(
              REFERRAL_ACTION_BUTTON_CLASS,
              'w-full lg:w-47.5',
              isBinding && 'disabled:bg-action-button disabled:opacity-100',
            )}
          >
            {isBinding ? (
              <span className='flex items-center gap-3'>
                <span className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                Binding ...
              </span>
            ) : (
              'Use Code and Bind'
            )}
          </Button>
        </div>
      )}
    </ReferralActionRow>
  );
};

export default ReferralInviteeStatus;
